import { Alert, AlertIcon, Box, Button, Icon, Link, List, ListIcon, ListItem, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiExternalLink } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { MarketDialogueStatus, type MarketDialogueDto } from "../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../urls";
import { DoffinRefusalAlert } from "../sourcing-event/result-notice/DoffinRefusalAlert";
import { type ChangedElsewhereRefusal } from "./changedElsewhere";
import type { MarketDialoguePublishing } from "./useMarketDialoguePublish";
import { ORGANIZATION_PROFILE_BLOCKERS, useMarketDialogueBlockerLabel } from "./marketDialogueLabels";
import { MarketDialogueFormSection } from "./MarketDialogueFormSection";
import { stepOfBlocker, type MarketDialogueStep } from "./marketDialogueSteps";

interface Props {
  dialogue: MarketDialogueDto;
  publishing: Pick<MarketDialoguePublishing, "refusalBreaches" | "noAnswer">;
  refusedAsChangedElsewhere: ChangedElsewhereRefusal | null;
  onReload: () => Promise<unknown>;
  reloadFailed: boolean;
  isReloading: boolean;
  onGoToStep?: (step: MarketDialogueStep) => void;
  stepLabel: (step: MarketDialogueStep) => string;
}

export const MarketDialoguePublishPanel = ({
  dialogue,
  publishing,
  refusedAsChangedElsewhere,
  onReload,
  reloadFailed,
  isReloading,
  onGoToStep,
  stepLabel,
}: Props) => {
  const { t } = useTranslation();
  const { refusalBreaches, noAnswer } = publishing;
  const blockerLabel = useMarketDialogueBlockerLabel(dialogue.canAnnounceOnDoffin);
  const displayDateWithTime = useDisplayDateWithTime();
  const needsProfile = dialogue.blockers.some((blocker) => ORGANIZATION_PROFILE_BLOCKERS.includes(blocker));

  return (
    <MarketDialogueFormSection heading={dialogue.announceOnDoffin ? t("Announcement on Doffin") : t("Open to invited suppliers")}>
      <Stack spacing="3">
        {!dialogue.announceOnDoffin && (
          <Text fontSize="sm" color="smMuted">
            {t("The market dialogue is not announced. Only the suppliers you invite can see it and answer.")}
          </Text>
        )}
        {dialogue.status === MarketDialogueStatus.PublicationPending && dialogue.submittedAt && (
          <Alert status="info" rounded="md" fontSize="sm">
            <AlertIcon />
            {t("Sent to Doffin {{date}}. Doffin publishes the notice shortly.", {
              date: displayDateWithTime(dialogue.submittedAt),
            })}
          </Alert>
        )}
        {dialogue.status === MarketDialogueStatus.PublicationFailed && (
          <Alert status="error" rounded="md" fontSize="sm">
            <AlertIcon />
            {t("Doffin did not publish the notice. Check the market dialogue and send it again.")}
          </Alert>
        )}
        {dialogue.doffinReference && (
          <Text fontSize="sm">
            {t("Doffin reference")}: {dialogue.doffinReference}
          </Text>
        )}
        {dialogue.announceOnDoffin && dialogue.status !== MarketDialogueStatus.Draft && (
          <Link href={dialogue.dialogueUrl} isExternal fontSize="sm" color="smPrimary">
            {t("The link suppliers follow from Doffin")} <Icon as={FiExternalLink} />
          </Link>
        )}
        {dialogue.canEdit && dialogue.blockers.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb="1">
              {t("Before you can publish")}
            </Text>
            <List spacing="1" fontSize="sm" data-testid="market-dialogue-blockers">
              {dialogue.blockers.map((blocker) => {
                const step = stepOfBlocker(blocker);
                return (
                  <ListItem key={blocker}>
                    <ListIcon as={FiAlertCircle} color="smWarning" />
                    {blockerLabel(blocker)}
                    {step && onGoToStep && (
                      <>
                        {" "}
                        <Button size="sm" variant="link" colorScheme="teal" onClick={() => onGoToStep(step)}>
                          {t("Go to «{{step}}»", { step: stepLabel(step) })}
                        </Button>
                      </>
                    )}
                  </ListItem>
                );
              })}
            </List>
            {needsProfile && (
              <Text fontSize="sm" mt="2" data-testid="market-dialogue-profile-hint">
                {t(
                  "The organisation details are filled in on the organisation profile. If you cannot edit it, ask an administrator in your organisation.",
                )}{" "}
                <Link as={RouterLink} to={urls.app.organizationProfileEdit} color="smPrimary">
                  {t("Open the organisation profile")}
                </Link>
              </Text>
            )}
          </Box>
        )}
        {refusalBreaches && (
          <DoffinRefusalAlert
            rounded="md"
            fontSize="sm"
            breaches={refusalBreaches}
            heading={t("Doffin refused the notice. Nothing has been published.")}
            advice={t("Correct the market dialogue and publish again, or contact support.")}
          />
        )}
        {noAnswer && (
          <Alert status={noAnswer === "whileSending" ? "warning" : "error"} rounded="md" fontSize="sm" alignItems="start">
            <AlertIcon />
            {noAnswer === "whileSending"
              ? t(
                  "Doffin did not answer as it should while the notice was being sent, so we cannot tell whether it arrived. The problem is at Doffin, not in the market dialogue. If the market dialogue now shows as pending publication, Doffin received it and the status updates once Doffin publishes it. If it is still a draft, nothing was sent and you can publish again.",
                )
              : t("Doffin did not answer as it should, so nothing was sent. The problem is at Doffin, not in the market dialogue. Try publishing again in a few minutes.")}
          </Alert>
        )}
        {refusedAsChangedElsewhere && (
          <Alert status="warning" rounded="md" fontSize="sm" alignItems="start">
            <AlertIcon />
            <Stack spacing="3" align="flex-start">
              <Text>
                {refusedAsChangedElsewhere === "publish"
                  ? t(
                      "The market dialogue was changed in another tab or by a colleague, so nothing was sent to Doffin. Reload to see the latest version.",
                    )
                  : t("The market dialogue was changed in another tab or by a colleague. Reload to see the latest version before you publish.")}
              </Text>
              {reloadFailed && <Text color="smError">{t("Could not reload the market dialogue. Try again.")}</Text>}
              <Button size="sm" onClick={() => void onReload()} isLoading={isReloading}>
                {t("Reload")}
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </MarketDialogueFormSection>
  );
};
